import React from "react";
import axios from "axios";
import { toast } from "sonner";
import { Shield, Send, Eye, AlertTriangle, CheckCircle2, Calendar } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  rejected_driver_applications: "Afviste/ubrugte chauffør-ansøgninger",
  old_employment_records: "Medarbejder-data (5+ år)",
  old_contact_messages: "Kontakt-beskeder (3+ år)",
  old_bookings: "Afsluttede bookings (5+ år)",
  unused_onboarding_tokens: "Ubrugte onboarding-tokens (90+ dage)",
  old_winback_records: "Win-back email-historik (2+ år)",
};

export default function GdprPanel({ token }) {
  const [scan, setScan] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [sending, setSending] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/gdpr-report/preview`, { headers: { Authorization: `Bearer ${token}` } });
      setScan(data);
    } catch (e) {
      toast.error("Kunne ikke hente GDPR-scan");
    } finally {
      setLoading(false);
    }
  }, [token]);

  React.useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!confirm("Send GDPR-rapport via email til job@emakurer.dk + kontakt@emakurer.dk?")) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/gdpr-report/send`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (data.ok) toast.success(`Rapport sendt til ${data.sent_to.join(", ")}`);
    } catch (e) {
      toast.error("Kunne ikke sende rapport");
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="text-brand-text2" data-testid="gdpr-loading">Indlæser GDPR-scan...</div>;
  if (!scan) return <div className="text-brand-text2">Kunne ikke hente data</div>;

  const total = scan.total_items_to_review || 0;
  const allClear = total === 0;

  return (
    <div data-testid="gdpr-panel" className="space-y-5">
      {/* Header */}
      <div className="bg-white border border-brand p-5">
        <div className="flex items-center gap-3">
          <Shield size={22} className="text-brand-red" />
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest text-brand-text2 font-semibold">GDPR Compliance</div>
            <div className="font-display text-lg text-brand-dark">Månedlig sletnings-rapport</div>
          </div>
          <div className="text-right">
            <div className="text-xs text-brand-text2 flex items-center gap-1 justify-end"><Calendar size={11} /> {scan.period_label}</div>
            <div className={`font-display font-bold text-2xl ${allClear ? "text-emerald-600" : "text-brand-red"}`} data-testid="gdpr-total">{total}</div>
            <div className="text-xs text-brand-text2">til gennemgang</div>
          </div>
        </div>
      </div>

      {allClear ? (
        <div className="bg-emerald-50 border border-emerald-200 p-6 flex items-center gap-3" data-testid="gdpr-allclear">
          <CheckCircle2 size={24} className="text-emerald-700 shrink-0" />
          <div>
            <div className="font-semibold text-emerald-900">Alt i orden — ingen handlinger påkrævet 🌱</div>
            <div className="text-sm text-emerald-800 mt-1">Alle persondata er inden for de definerede opbevaringsperioder. Næste automatiske rapport: den 1. i næste måned kl. 09:30.</div>
          </div>
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 p-6 flex items-start gap-3">
          <AlertTriangle size={24} className="text-amber-700 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-amber-900">{total} records bør gennemgås</div>
            <div className="text-sm text-amber-800 mt-1">Nedenstående kategorier indeholder persondata der har overskredet vores opbevaringsperioder iht. GDPR Art. 5(1)(e). Gennemgå og slet/anonymiser hvor relevant.</div>
          </div>
        </div>
      )}

      {/* Categories */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {Object.entries(scan.findings || {}).map(([key, f]) => {
          const label = CATEGORY_LABELS[key] || key;
          const isEmpty = !f.count;
          return (
            <div key={key} className={`bg-white border ${isEmpty ? "border-brand opacity-60" : "border-brand-red"} p-4`}>
              <div className="flex items-start justify-between mb-2">
                <div className="font-semibold text-brand-dark text-sm">{label}</div>
                <div className={`font-display font-bold text-xl ${isEmpty ? "text-brand-text2" : "text-brand-red"}`}>{f.count}</div>
              </div>
              <div className="text-xs text-brand-text2 mt-1 leading-relaxed">{f.reason}</div>
              {!isEmpty && (
                <div className="text-xs text-brand-dark mt-2 bg-brand-bg p-2 leading-relaxed">
                  <strong>Handling:</strong> {f.action}
                </div>
              )}
              <div className="text-[10px] text-brand-text2 mt-2 uppercase tracking-widest">Opbevaring: {f.retention_days} dage</div>
            </div>
          );
        })}
      </div>

      {/* Actions */}
      <div className="flex gap-3 flex-wrap">
        <button onClick={load} className="btn-ghost-dark text-sm" data-testid="gdpr-refresh">
          <Eye size={14} /> Genscan nu
        </button>
        <button onClick={send} disabled={sending} className="btn-primary text-sm" data-testid="gdpr-send">
          {sending ? "Sender..." : <><Send size={14} /> Send rapport via email</>}
        </button>
      </div>

      <p className="text-xs text-brand-text2 leading-relaxed bg-brand-bg p-4">
        💡 <strong>Automatisk:</strong> Denne rapport sendes automatisk den 1. i hver måned kl. 09:30 til <code>job@emakurer.dk</code> og <code>kontakt@emakurer.dk</code>. PDF-versionen er klar til arkivering hvis Datatilsynet anmoder om dokumentation for jeres sletnings-praksis (GDPR Art. 5(2) "accountability").
      </p>
    </div>
  );
}
